import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { ADMIN_SIGNALS_PATH, DISPLAY_CURRENCIES } from "common/constants";
import { useRouteMatch } from "react-router-dom";
import { getSignalExtraDetailsRoute } from "utils/routes";
import Table from "common/Table/Table";
import useTableRowsSelector from "utils/hooks/useTableRowsSelector";
import { SignalActionsContext } from "@shared/features/signals/common/SignalActions.context";
import { formatDate } from "@shared/utils/date";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup";
import {
  AnalyticsPageName,
  PrecisionConfig,
  PrecisionType,
  Signal,
  SortingOrder,
  LanguageNameSpaces,
} from "@shared/types";
import SignalActionsDropdown from "features/signals/SignalActionsDropdown";
import Text from "common/Typography/Text";
import SignalFollowActions from "features/signals/SignalFollow/SignalFollowActions";
import { RootState } from "app/store";
import { SignalFollowAlertIcon } from "features/signals/SignalFollow/SignalFollowAlertIcon";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import TextWithIcon from "common/Text/TextWithIcon";
import TextWithPrecision from "common/Text/TextWithPrecision";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  ICON_TYPES,
  SIGNAL_EXTRA_DETAILS_TABS,
} from "@shared/common/constants";
import { checkSymbolSearch } from "@shared/utils/misc";
import { TableLoadingEmpty } from "common/styles";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { ColumnsType } from "antd/es/table";
import { FROM_LAPTOP_COLUMN } from "common/NewLook/responsiveColumns.ts";
import SignalsEmptyState from "features/signals/SignalsEmptyState";
import { SignalsEmptyStateProps } from "features/signals/common/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useDisplayTradeDetails } from "@shared/utils/hooks/misc.ts";
import { useTheme } from "styled-components";
import { directionColor, getNewLookTokens } from "common/NewLook/tokens.ts";
import NewLookSignalSymbol from "features/signals/NewLook/NewLookSignalSymbol.tsx";
import {
  FigureCell,
  TimeCell,
  TradeRowsSkeleton,
} from "features/trades/NewLook/NewLookTradeCells.tsx";
import { MINUS } from "features/trades/NewLook/useMoneyFormat.ts";

interface SignalTableProps {
  isLoading: boolean;
  isOpen: boolean;
  precisionSelector: (state: RootState) => PrecisionConfig | undefined;
  searchFilter?: string;
  signalsData?: Signal[];
  showEmpty?: boolean;
  emptyStateProps: SignalsEmptyStateProps;
}

const SignalsTable = ({
  signalsData,
  isLoading,
  precisionSelector,
  isOpen,
  searchFilter = "",
  showEmpty,
  emptyStateProps,
}: SignalTableProps) => {
  const { selectedSignalIDs, setSelectedSignalIDs, showEditSignalActions } =
    useContext(SignalActionsContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { historyPush } = useHistoryWithQuery();
  const isAdminSignals = !!useRouteMatch(ADMIN_SIGNALS_PATH);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const displayTradeDetails = useDisplayTradeDetails();
  const theme = useTheme();
  const { isNewLook } = theme;

  const volumeCurrency =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? DISPLAY_CURRENCIES.BTC
      : DISPLAY_CURRENCIES.USD;

  const volumeSortPath =
    displayCurrency === DISPLAY_CURRENCIES.USD
      ? "trading_volume.USD"
      : "trading_volume.BTC";

  const adminColumns =
    isAdminSignals && isOpen
      ? [
          {
            title: t("table.columns.no_of_trades"),
            dataIndex: "num_of_trades",
            key: "num_of_trades",
            showSorterTooltip: false,
            ...(isNewLook && NEW_LOOK_END_COLUMN),
            render: (numOfTrades: number) =>
              isNewLook ? <FigureCell value={numOfTrades} /> : numOfTrades,
            sorter: (a: Signal, b: Signal) =>
              sortByNumberProperty(a, b, "num_of_trades"),
          },
          {
            title: (
              <div style={{ display: "flex" }}>
                {t("table.columns.volume")}
                <HelperTooltip
                  analyticsFieldName="Volume"
                  analyticsPageName={AnalyticsPageName.SIGNALS}
                  description={t("tooltips.volume")}
                />
              </div>
            ),
            dataIndex: ["trading_volume", volumeCurrency],
            key: "trading_volume",
            showSorterTooltip: false,
            ...(isNewLook && NEW_LOOK_END_COLUMN),
            render: (volume: number) => {
              const value = (
                <TextWithPrecisionAndIcon
                  precisionSelector={precisionSelector}
                  text={volume}
                  precisionType={PrecisionType.BALANCE}
                  round={true}
                />
              );
              return isNewLook ? <FigureCell value={value} /> : value;
            },
            sorter: (a: Signal, b: Signal) =>
              sortByNumberProperty(a, b, volumeSortPath),
          },
        ]
      : [];

  const userColumns = !isAdminSignals
    ? [
        {
          title: t("table.columns.actions"),
          showSorterTooltip: false,
          width: isOpen ? 127 : 80,
          render: (signalData: Signal) => (
            <SignalFollowActions signal={signalData} isDrawer={false} />
          ),
        },
      ]
    : [];

  const columns: ColumnsType<Signal> = [
    {
      title: t("table.columns.symbol"),
      dataIndex: "symbol",
      key: "symbol",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "symbol"),
      render: (_, signal) =>
        isNewLook ? (
          <NewLookSignalSymbol signal={signal} />
        ) : (
          <div style={{ display: "flex", alignItems: "center" }}>
            <TextWithIcon
              text={signal.symbol}
              coinOrExchangeName={signal.symbol}
              iconType={ICON_TYPES.COIN}
              iconSize={"l"}
            />
            <SignalFollowAlertIcon signal={signal} />
          </div>
        ),
    },
    {
      title: t("table.columns.exchanges"),
      dataIndex: "exchanges",
      key: "exchanges",
      showSorterTooltip: false,
      ...(isNewLook && FROM_LAPTOP_COLUMN),
      render(exchanges, _) {
        return <ExchangesIconsGroup exchanges={exchanges} />;
      },
    },
    {
      title: isOpen
        ? t("table.columns.price")
        : t("table.columns.closed_price"),
      dataIndex: "price",
      showSorterTooltip: false,
      ...(isNewLook && NEW_LOOK_END_COLUMN),
      render: (price, coinData) => {
        const value =
          price != null ? (
            <TextWithPrecisionAndIcon
              currency={DISPLAY_CURRENCIES.USD}
              precisionSelector={precisionSelector}
              precisionType={PrecisionType.PRICE}
              text={price}
              precisionCoin={coinData["symbol"]}
              exchange={coinData["exchange"]}
              round={true}
            />
          ) : (
            <Text>{t("table.na")}</Text>
          );
        return isNewLook ? <FigureCell value={value} /> : value;
      },
      sorter: (a, b) => sortByNumberProperty(a, b, "price"),
    },
    ...adminColumns,
    {
      title: (
        <div style={{ display: "flex" }}>
          {t("table.columns.profit")}
          <HelperTooltip
            analyticsFieldName="Profit"
            analyticsPageName={AnalyticsPageName.SIGNALS}
            description={t("tooltips.profit")}
          />
        </div>
      ),
      dataIndex: "graph_data",
      showSorterTooltip: false,
      ...(isNewLook && NEW_LOOK_END_COLUMN),
      render: (graphData) =>
        isNewLook ? (
          <FigureCell
            color={directionColor(getNewLookTokens(theme), graphData.profit)}
            value={
              <>
                {graphData.profit > 0 ? "+" : graphData.profit < 0 ? MINUS : ""}
                <TextWithPrecision
                  text={Math.abs(graphData.profit)}
                  precisionSelector={precisionSelector}
                  precisionType={PrecisionType.PERCENT}
                  round={true}
                />
                %
              </>
            }
          />
        ) : (
          <>
            <TextWithPrecision
              text={graphData.profit}
              precisionSelector={precisionSelector}
              precisionType={PrecisionType.PERCENT}
              round={true}
            />
            %
          </>
        ),
      sorter: (a, b) => sortByNumberProperty(a, b, "graph_data.profit"),
    },
    {
      title: isOpen
        ? t("table.columns.last_updated")
        : t("table.columns.closed_at"),
      dataIndex: "relevant_time",
      defaultSortOrder: "descend",
      showSorterTooltip: false,
      ...(isNewLook && FROM_LAPTOP_COLUMN),
      render: (relevantTime) =>
        isNewLook ? (
          <TimeCell>{formatDate(relevantTime, true, true)}</TimeCell>
        ) : (
          <>{formatDate(relevantTime, true, true)}</>
        ),
      sorter: (a, b) =>
        sortByNumberProperty(a, b, "relevant_time", SortingOrder.ASCEND),
    },
    ...userColumns,
  ];

  if (showEditSignalActions)
    columns.push({
      title: "",
      showSorterTooltip: false,
      width: 40,
      render: (signalRowData) => (
        <SignalActionsDropdown signal={signalRowData} />
      ),
    });

  const filteredData = signalsData
    ?.filter((signalData) => {
      return checkSymbolSearch(signalData.symbol, searchFilter);
    })
    .map((signalData) => ({ ...signalData, key: signalData.signal_id }));

  const { tableRowProps } = useTableRowsSelector<Signal, number>({
    selectedRows: selectedSignalIDs,
    setSelectedRows: setSelectedSignalIDs,
    getKey: (signal: Signal) => signal.signal_id,
    allRows: signalsData,
    filteredRows: filteredData,
  });

  // New look: rows shaped like the content while the first page loads,
  // instead of a spinner over an empty table.
  const showSkeleton = isNewLook && isLoading && !signalsData?.length;
  let emptyText: React.ReactNode = <TableLoadingEmpty />;
  if (showEmpty)
    emptyText = (
      <SignalsEmptyState
        {...emptyStateProps}
        isSearch={!!searchFilter?.length}
      />
    );
  if (showSkeleton) emptyText = <TradeRowsSkeleton />;
  const locale = { emptyText };

  return (
    <Table<Signal>
      columns={columns}
      dataSource={filteredData}
      loading={isLoading && !showSkeleton}
      rowClassName={displayTradeDetails ? "u-cursorPointer" : undefined}
      rowKey={(signal) => signal.signal_id}
      rowSelection={showEditSignalActions ? tableRowProps : undefined}
      onRow={(signal) => {
        const openSignal = () => {
          if (displayTradeDetails) {
            historyPush(
              getSignalExtraDetailsRoute(
                location.pathname,
                signal.signal_id,
                SIGNAL_EXTRA_DETAILS_TABS.GENERAL,
                false
              )
            );
          }
        };
        return {
          onClick: openSignal,
          // New look: rows that open the drawer are reachable by keyboard.
          ...(isNewLook &&
            displayTradeDetails && {
              tabIndex: 0,
              onKeyDown: (event: React.KeyboardEvent) => {
                if (
                  event.key === "Enter" &&
                  event.target === event.currentTarget
                )
                  openSignal();
              },
            }),
        };
      }}
      itemsType={t("table.item_type")}
      locale={locale}
    />
  );
};
export default SignalsTable;

// New look: figure columns are right-aligned, titles over their figures.
const NEW_LOOK_END_COLUMN = { align: "right", className: "nl-end" } as const;
